import React, { FunctionComponent, useState, useEffect, useMemo } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert,Box, Button, Collapse, Grid, IconButton, Link, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import { useGetCompanyPurchaseOrderStatus, useGetCompanyOrderStatusList } from '../../hooks/api/oms';
import moment from 'moment';
import { useFindInvoice } from '../../hooks/api/invoice';
import { useQueryClient } from '@tanstack/react-query';

export interface OrderStatusValue {
  orderNumber: string;
  customerPo: string | null;
  orderType: string;
  orderHeader: OrderHeader;
  invoices: any; // You can replace 'any' if you have invoice details
  lineItems: LineItem[];
  customerInfo: CustomerInfo;
  shippingInfo: ShippingInfo;
  freightInfo: FreightInfo;
  comments: Comment[];
  orderTotals: OrderTotals;
  legalDisclaimers: string[];
}

export interface OrderHeader {
  orderNumber: string;
  customerPo: string;
  customer: string;
  shipTo: string;
  fob: string;
  freightTerms: string;
  freightTermsDescription: string | null;
  remarks: string;
  orderDate: string;
  trailerAmount: number;
  trailerCode: string;
  trailerDescription: string | null;
  commentIndex: string;
  creditTerms: string;
  salesPerson: string;
  customerSalesPerson: string;
  region: string;
  status: string;
  confirmationDate: string;
  freightList: string | null;
  freightListDescription: string | null;
}

export interface LineItem {
  lineNumber: string;
  partNumber: string;
  description: string;
  quantity: number;
  quantityShipped: number;
  quantityInvoiced: number;
  quantityOpen: number;
  partialShipment: boolean;
  shippingToday: boolean;
  dueDate: string;
  dueDateStatus: string;
  dueDateDisplayMessage: string | null; // Display message for special dates
  overdue: boolean;
  promiseDate: string | null;
  shipDate: string | null;
  unitPrice: number;
  extendedPrice: number;
  unitOfMeasure: string;
  status: string;
  commentIndex: string;
  freightDescription: string;
  billOfLading: string;
  freightList: string;
  invoiceNumber: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  carrier: string | null;
  comments: string[];
  itemType: string;
}

export interface CustomerInfo {
  customerNumber: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  fax: string | null;
}

export interface ShippingInfo {
  shipToNumber: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  fax: string | null;
}

export interface FreightInfo {
  freightTerms: string;
  freightTermsDescription: string;
  freightList: string | null;
  freightListDescription: string | null;
}

export interface Comment {
  print: string;
  sequence: number;
  lines: string[];
  fullText: string;
}

export interface OrderTotals {
  subtotalOpen: number;
  subtotalShipped: number;
  miscCharges: any[];
  grandTotal: number;
}

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

const TableHeadCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
  color: '#ffffff'
}));

const CheckPurchaseOrderStatusDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { poNumber } = useParams();
  
  const [shippedExpand, setShippedExpand] = useState<Record<string, boolean>>({});
  const [allShippedToggleOpen, setAllShippedToggleOpen] = useState(false);

  const [openExpand, setOpenExpand] = useState<Record<string, boolean>>({});
  const [allOpenToggleOpen, setAllOpenToggleOpen] = useState(false);

  const [fetchError, setFetchError] = useState<string | null>(null);
  const { data: orderStatusList, isFetched: hasFetched, isLoading, error } = useGetCompanyOrderStatusList(undefined, poNumber);
  
  // Deduplicate orders based on SO number and customer
  const uniqueOrders = React.useMemo(() => {
    if (!orderStatusList || orderStatusList.length === 0) return [];
    
    const seen = new Set();
    return orderStatusList.filter((order: any) => {
      const key = `${order.orderHeader?.orderNumber}-${order.orderHeader?.customer}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [orderStatusList]);

  // Handle multiple results - if more than one unique order, redirect to multiple results page
  useEffect(() => {
    if (hasFetched && uniqueOrders && uniqueOrders.length > 1) {
      navigate(`/order-management-system/check-order-status/po/${poNumber}`);
    }
  }, [hasFetched, uniqueOrders, navigate, poNumber]);
  
  // Use first result if only one unique order found
  const orderStatus = uniqueOrders && uniqueOrders.length === 1 ? uniqueOrders[0] : null;

  const toggleShipItem = (id: string) => {
    setShippedExpand((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleOpenItem = (id: string) => {
    setOpenExpand((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (hasFetched && !isLoading) {
      if (error) {
        // Handle API errors (including access denied)
        const axiosError = error as any; // Type assertion for axios error
        if (axiosError?.response?.status === 403) {
          // Access denied error
          const errorMessage = axiosError?.response?.data?.error || axiosError?.response?.data?.message || 'Access denied';
          setFetchError(errorMessage);
        } else {
          // Other API errors
          const errorMessage = axiosError?.response?.data?.error || axiosError?.response?.data?.message || 'Error retrieving order status';
          setFetchError(errorMessage);
        }
      } else if (!uniqueOrders || uniqueOrders.length === 0) {
        setFetchError(`No order found for PO Number: ${poNumber}`);
      } else {
        setFetchError(null); // Clear any previous error
      }
    }
  }, [hasFetched, isLoading, uniqueOrders, poNumber, error]);

  const shippedRows: LineItem[] = (orderStatus?.lineItems || []).filter((item: LineItem) => item.status === 'Shipped');
  const shippedTotal: number = shippedRows.reduce((sum: number, item: LineItem) => sum + (item.extendedPrice || 0), 0);
  const openItemRows: LineItem[] = (orderStatus?.lineItems || []).filter((item: LineItem) => item.status === 'Open');

  const toggleAllShippedRows = () => {
    const newAllOpen = !allShippedToggleOpen;
    const newOpenRows: Record<string, boolean> = {};

    shippedRows?.forEach((item, index) => {
      const id = `${item.lineNumber}_${index}`;
      newOpenRows[id] = newAllOpen;
    });

    setShippedExpand(newOpenRows);
    setAllShippedToggleOpen(newAllOpen);
  };

  const toggleAllOpenRows = () => {
    const newAllOpen = !allOpenToggleOpen;
    const newOpenRows: Record<string, boolean> = {};

    openItemRows?.forEach((item, index) => {
      const id = `${item.lineNumber}_${index}`;
      newOpenRows[id] = newAllOpen;
    });

    setOpenExpand(newOpenRows);
    setAllOpenToggleOpen(newAllOpen);
  };

  const { mutateAsync: findInvoice } = useFindInvoice();
 
   const handleDownloadInvoice = async (invoiceNumber: string | null) => {
     if (!invoiceNumber) {
       console.error('No invoice number provided.');
       return;
     }
     try {
       const response = await findInvoice({ partNo: invoiceNumber });
 
       const blob = new Blob([response.data], { type: 'application/pdf' });
       const url = window.URL.createObjectURL(blob);
 
       const link = document.createElement('a');
       link.href = url;
       link.setAttribute('download', `${invoiceNumber}.pdf`);
       document.body.appendChild(link);
       link.click();
       link.remove();
 
       window.URL.revokeObjectURL(url);
     } catch (error:any) {
       let errorMessage = 'Failed to download invoice. Please try again.';
       if (
         error?.response?.data instanceof Blob &&
         error?.response?.data.type === 'application/json'
       ) {
         try {
           const errorText = await error.response.data.text();
           const errorJson = JSON.parse(errorText);
           errorMessage = `${errorJson.message ?? ''}${errorJson.error ? `: ${errorJson.error}` : ''}`.trim();
         } catch {
           errorMessage = 'Failed to read server error response.';
         }
       } else if (error?.response?.data?.error || error?.response?.data?.message) {
         errorMessage = `${error.response.data.message ?? ''}${error.response.data.error ? `: ${error.response.data.error}` : ''}`.trim();
       } else if (error?.message) {
         errorMessage = error.message;
       }
       alert(errorMessage);
     }
   };
 
  const handleBack = () => {
    queryClient.clear();
    navigate(-1);
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      alert('Please allow popups to print this order');
      return;
    }

    // Create the print content HTML with identical styling to the current page
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Purchase Order ${orderStatus?.orderHeader?.orderNumber || 'N/A'} - Print Version</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white; 
              color: black;
              line-height: 1.1;
            }
            .container {
              max-width: 100%;
              margin: 0;
              padding: 10px;
            }
            .header { 
              background: #c3183c; 
              color: white; 
              padding: 8px 12px; 
              margin-bottom: 8px;
              border-radius: 0;
            }
            .header h1 { 
              margin: 0; 
              font-size: 16px; 
              font-weight: bold;
            }
            .info-section { 
              background: #f7f5f7; 
              border: 1px solid #dbdbdb; 
              margin-bottom: 8px;
              border-radius: 0;
            }
            .info-section-header { 
              background: #f7f5f7; 
              padding: 6px 12px; 
              border-bottom: 1px solid #dbdbdb;
              font-weight: bold;
            }
            .info-section-content { 
              padding: 8px 12px;
            }
            .grid-container { 
              display: grid; 
              grid-template-columns: 1fr 1fr 1fr; 
              gap: 12px;
            }
            .grid-item h3 { 
              margin: 0 0 4px 0; 
              color: #c3183c; 
              font-size: 12px;
            }
            .grid-item p { 
              margin: 2px 0; 
              line-height: 1.1;
              font-size: 10px;
            }
            .comments { 
              margin-top: 8px;
            }
            .comments h3 { 
              margin: 0 0 4px 0; 
              color: #c3183c;
              font-size: 12px;
            }
            .comments p {
              margin: 2px 0;
              font-size: 10px;
              line-height: 1.1;
            }
            .table-container { 
              margin: 8px 0;
            }
            .table-header { 
              background: #c3183c; 
              color: white; 
              padding: 6px 12px; 
              border-radius: 0;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .table-header h2 { 
              margin: 0; 
              font-size: 14px;
            }
            .table-header .expand-info { 
              color: #c3183c; 
              font-size: 10px;
              background: white;
              padding: 2px 6px;
              border-radius: 2px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 4px;
            }
            th { 
              background: #c3183c; 
              color: white; 
              padding: 6px 4px; 
              text-align: left; 
              font-weight: bold;
              font-size: 10px;
            }
            td { 
              padding: 4px 4px; 
              border-bottom: 1px solid #ddd;
              font-size: 9px;
            }
            .expand-details { 
              background: #ebebeb; 
              padding: 6px; 
              border-top: 1px solid #ddd;
            }
            .expand-details .grid-container { 
              display: grid; 
              grid-template-columns: 1fr 1fr 1fr; 
              gap: 8px;
            }
            .expand-details h4 { 
              margin: 0 0 4px 0; 
              color: #c3183c;
              font-size: 9px;
            }
            .total-row { 
              background: black; 
              color: white; 
              font-weight: bold;
            }
            .open-total { 
              background: #f0f0f0; 
              color: black; 
              padding: 15px; 
              text-align: right; 
              font-weight: bold; 
              border-radius: 0 0 4px 4px;
              border: 1px solid #dbdbdb;
              border-top: none;
              font-size: 12px;
            }
            .shipped-total { 
              background: black; 
              color: white; 
              padding: 15px; 
              text-align: right; 
              font-weight: bold; 
              border-radius: 0 0 4px 4px;
              border: 1px solid #dbdbdb;
              border-top: none;
              font-size: 12px;
            }
            .legal-disclaimers { 
              margin: 8px 0;
              padding: 8px;
              background: #f9f9f9;
              border: 1px solid #ddd;
              border-radius: 0;
            }
            .legal-disclaimers h3 { 
              margin: 0 0 6px 0; 
              color: #c3183c;
              font-size: 12px;
            }
            .legal-disclaimers p { 
              margin: 4px 0; 
              line-height: 1.2;
              font-size: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <!-- Header - Exact same as current page -->
            <div class="header">
              <div style="flex: 1;">
                <h1>Order Information</h1>
              </div>
            </div>

            <!-- Info Section - Exact same as current page -->
            <div class="info-section">
              <div class="info-section-header">
                <div class="grid-container">
                  <div><strong>Sales Order: ${orderStatus?.orderHeader?.orderNumber || 'N/A'}</strong></div>
                  <div><strong>Order Date: ${orderStatus?.orderHeader?.orderDate ? moment(orderStatus.orderHeader.orderDate).format('MM/DD/YYYY') : 'N/A'}</strong></div>
                  <div><strong>Purchase Order: ${orderStatus?.orderHeader?.customerPo || 'N/A'}</strong></div>
                </div>
              </div>
              <div class="info-section-content">
                <div class="grid-container">
                  <div class="grid-item">
                    <h3>Bill to:</h3>
                    <p><strong>${orderStatus?.customerInfo?.name || 'N/A'}</strong></p>
                    <p>${orderStatus?.customerInfo?.address1 || ''}</p>
                    <p>${orderStatus?.customerInfo?.address2 || ''}</p>
                    <p>${[
                      orderStatus?.customerInfo?.city,
                      orderStatus?.customerInfo?.state,
                      orderStatus?.customerInfo?.country,
                      orderStatus?.customerInfo?.zip
                    ].filter(Boolean).join(', ') || 'N/A'}</p>
                  </div>
                  <div class="grid-item">
                    <h3>Ship to:</h3>
                    <p><strong>${orderStatus?.shippingInfo?.name || 'N/A'}</strong></p>
                    <p>${orderStatus?.shippingInfo?.address1 || ''}</p>
                    <p>${orderStatus?.shippingInfo?.address2 || ''}</p>
                    <p>${[
                      orderStatus?.shippingInfo?.city,
                      orderStatus?.shippingInfo?.state,
                      orderStatus?.shippingInfo?.country,
                      orderStatus?.shippingInfo?.zip
                    ].filter(Boolean).join(', ') || 'N/A'}</p>
                  </div>
                  <div class="grid-item">
                    <h3>Shipping Details:</h3>
                    <p>Shipping Terms: ${orderStatus?.orderHeader?.freightTerms || 'N/A'}</p>
                    <p>Shipping Account: ${orderStatus?.orderHeader?.remarks || 'N/A'}</p>
                    <p>FOB: ${orderStatus?.orderHeader?.fob || 'N/A'}</p>
                    <p>Credit Terms: ${orderStatus?.orderHeader?.creditTerms || 'N/A'}</p>
                  </div>
                </div>
                
                ${orderStatus?.comments && orderStatus.comments.length > 0 
                  ? `<div class="comments">
                      <h3>Order Comments:</h3>
                      ${orderStatus.comments.map((comment: any) => 
                        comment.lines.map((line: string) => `<p style="margin: 2px 0; line-height: 1.2;">${line}</p>`).join('')
                      ).join('')}
                    </div>`
                  : ''
                }
              </div>
            </div>

            <!-- Open Items Table - Exact same styling as current page -->
            <div class="table-container">
              <div class="table-header">
                <h2><b>Open Items</b></h2>
                <div class="expand-info">All Details Expanded</div>
              </div>
              ${openItemRows.length > 0 
                ? `<table>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Part Number</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Due Date</th>
                        <th>Est. Revised Ship Date</th>
                        <th>Net Price</th>
                        <th>Ext. Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${openItemRows.map((item: any, index: number) => `
                        <tr>
                          <td style="display: flex; align-items: center; gap: 8px;">
                            <span style="color: #c3183c; font-size: 20px;">▼</span>
                            ${item.lineNumber}
                          </td>
                          <td>${item.partNumber}</td>
                          <td>${item.description}</td>
                          <td>${item.quantity}</td>
                          <td>${item.dueDateDisplayMessage || (item.dueDate ? moment(item.dueDate).format('MM/DD/YYYY') : '')}</td>
                          <td>${item.promiseDate ? moment(item.promiseDate).format('MM/DD/YYYY') : ''}</td>
                          <td>${formatCurrency(item.unitPrice)}</td>
                          <td>${formatCurrency(item.extendedPrice)}</td>
                        </tr>
                        <tr>
                          <td colspan="8" style="padding: 0;">
                            <div class="expand-details">
                              <div class="grid-container">
                                <div>
                                  <h4>Ship Via: ${item.freightDescription || 'N/A'}</h4>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                  <div class="open-total">
                    Sub Total Open Items: ${formatCurrency(orderStatus?.orderTotals?.subtotalOpen || 0)}
                  </div>`
                : '<div style="padding: 20px; text-align: center; background: #f0f0f0; border: 1px solid #dbdbdb; border-top: none; border-radius: 0 0 4px 4px;">This order has no open items.</div>'
              }
            </div>

            <!-- Shipped Items Table - Exact same styling as current page -->
            <div class="table-container">
              <div class="table-header">
                <h2><b>Shipped Items</b></h2>
                <div class="expand-info">All Details Expanded</div>
              </div>
              ${shippedRows.length > 0 
                ? `<table>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Part Number</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Ship Date</th>
                        <th>Net Price</th>
                        <th>Ext. Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${shippedRows.map((item: any, index: number) => `
                        <tr>
                          <td style="display: flex; align-items: center; gap: 8px;">
                            <span style="color: #c3183c; font-size: 20px;">▼</span>
                            ${item.lineNumber}
                          </td>
                          <td>${item.partNumber}</td>
                          <td>${item.description}</td>
                          <td>${item.quantity}</td>
                          <td>${item.shipDate ? moment(item.shipDate).format('MM/DD/YYYY') : ''}</td>
                          <td>${formatCurrency(item.unitPrice)}</td>
                          <td>${formatCurrency(item.extendedPrice)}</td>
                        </tr>
                        <tr>
                          <td colspan="7" style="padding: 0;">
                            <div class="expand-details">
                              <div class="grid-container">
                                <div>
                                  <h4>Ship Via: ${item.freightDescription || 'N/A'}</h4>
                                </div>
                                <div>
                                  <h4>${(() => {
                                    const trackingInfo = getTrackingInfo(item);
                                    return trackingInfo.label + ' ' + trackingInfo.number;
                                  })()}</h4>
                                </div>
                                <div>
                                  <h4>Invoice #: ${item.invoiceNumber || 'N/A'}</h4>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                  <div class="shipped-total">
                    Sub Total Shipped Items: ${formatCurrency(shippedTotal)}
                  </div>`
                : '<div style="padding: 20px; text-align: center; background: #f0f0f0; border: 1px solid #dbdbdb; border-top: none; border-radius: 0 0 4px 4px;">No shipped items available.</div>'
              }
            </div>

            <!-- Legal Disclaimers - Exact same styling as current page -->
            ${orderStatus?.legalDisclaimers && orderStatus.legalDisclaimers.length > 0 
              ? `<div class="legal-disclaimers">
                  <h3>Legal Disclaimers</h3>
                  ${orderStatus.legalDisclaimers.map((disclaimer: string) => 
                    `<p>${disclaimer.replace(/https:\/\/apps\.barksdale\.com\/Barksdale-DynalcoStdCond\.htm/g, 'Standard Terms and Conditions')}</p>`
                  ).join('')}
                </div>`
              : ''
            }
          </div>
        </body>
      </html>
    `;

    // Write the content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Helper function to format currency with commas (legacy format)
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to get tracking information with fallback to bill of lading
  const getTrackingInfo = (item: LineItem) => {
    // Primary: Use trackingNumber and trackingUrl if available
    if (item.trackingNumber && item.trackingUrl) {
      return {
        label: "Tracking #:",
        number: item.trackingNumber,
        url: item.trackingUrl,
        type: "tracking"
      };
    }
    
    // Fallback: Use billOfLading if tracking info is not available
    if (item.billOfLading) {
      return {
        label: "Tracking #:",
        number: item.billOfLading,
        url: null,
        type: "billOfLading"
      }
    }
    
    // No tracking information available
    return {
      label: "Tracking #:",
      number: "N/A",
      url: null,
      type: "none"
    };
  };

  // Helper function to process legal disclaimers and handle Standard Terms and Conditions
  const processLegalDisclaimer = (disclaimer: string) => {
    const standardTermsUrl = 'https://apps.barksdale.com/Barksdale-DynalcoStdCond.htm';
    
    if (disclaimer.includes(standardTermsUrl)) {
      const parts = disclaimer.split(standardTermsUrl);
      return (
        <>
          {parts[0]}
          <Link
            href={standardTermsUrl}
            target="_blank"
            rel="noopener"
            sx={{ color: '#c3183c', textDecorationColor: '#c3183c' }}
          >
            Standard Terms and Conditions
          </Link>
          {parts[1]}
        </>
      );
    }
    
    // Handle other URLs normally (existing logic)
    if (disclaimer.includes('https://')) {
      const parts = disclaimer.split('https://');
      return (
        <>
          {parts[0]}
          <Link
            href={`https://${parts[1]}`}
            target="_blank"
            rel="noopener"
            sx={{ color: '#c3183c', textDecorationColor: '#c3183c' }}
          >
            {`https://${parts[1]}`}
          </Link>
        </>
      );
    }
    
    return disclaimer;
  };


  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
      {isLoading && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Loading order status details for PO: <strong>{poNumber}</strong>...
        </Alert>
      )}

      {!isLoading && fetchError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {fetchError}
        </Alert>
      )}
      {!isLoading && hasFetched && orderStatus && !fetchError && (
        <>
          {/* <Grid container spacing={2} sx={{ fontSize: '14px', fontWeight: 'bold' }}>
            Please Note This Acknowledgement Confirms Acceptance of Your Order, but Does Not Imply Acceptance of the Price. Items With a Ship Date of Jan 1, 2023 or After, May Be Subject to a Price Increase Prior to Shipment.
          </Grid> */}

            <Grid size={12} sx={{ mt: 1 }}>
              <Grid container bgcolor="#c3183c" color="white" px={2} py={1} alignItems="center">
                <Grid size={6}>
                  <Typography fontWeight="bold">Order Information</Typography>
                </Grid>
                <Grid size={6} display="flex" justifyContent="flex-end">
                  <Button
                    onClick={handlePrint}
                    variant="text"
                    sx={{ color: 'white', textTransform: 'none' }}
                    startIcon={<PrintIcon />}
                  >
                    <Box sx={{ borderBottom: '2px solid #e5496a' }}>Print Version</Box>
                  </Button>
                </Grid>
              </Grid>

              <Box bgcolor="#f7f5f7" sx={{ border: '1px solid #dbdbdb' }}>
                <Box px={2} py={1} sx={{ borderBottom: '1px solid #dbdbdb' }}>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <Typography fontWeight="bold">Sales Order: {orderStatus.orderHeader?.orderNumber}</Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography fontWeight="bold">Order Date: {moment(orderStatus.orderHeader?.orderDate ?? 'N/A').format('MM/DD/YYYY')}</Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography fontWeight="bold">Purchase Order: {orderStatus.orderHeader?.customerPo ?? 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box px={2} py={1} sx={{ borderBottom: '1px solid #dbdbdb' }}>
                  <Grid container spacing={3}>
                    <Grid size={4}>
                      <Typography fontWeight="bold" mb={0.5}>Bill to:</Typography>
                      <Typography>{orderStatus.customerInfo?.name ?? 'N/A'}</Typography>
                      <Typography>{orderStatus.customerInfo?.address1}</Typography>
                      <Typography>{orderStatus.customerInfo?.address2}</Typography>
                      <Typography>
                        {[
                          orderStatus.customerInfo?.city,
                          orderStatus.customerInfo?.state,
                          orderStatus.customerInfo?.country,
                          orderStatus.customerInfo?.zip
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography fontWeight="bold" mb={0.5}>Ship to:</Typography>
                      <Typography>{orderStatus.shippingInfo?.name ?? 'N/A'}</Typography>
                      <Typography>{orderStatus.shippingInfo?.address1}</Typography>
                      <Typography>{orderStatus.shippingInfo?.address2}</Typography>
                      <Typography>
                        {[
                          orderStatus.shippingInfo?.city,
                          orderStatus.shippingInfo?.state,
                          orderStatus.shippingInfo?.country,
                          orderStatus.shippingInfo?.zip
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography fontWeight="bold" mb={0.5}>Shipping Details:</Typography>
                      <Typography>Shipping Terms: {orderStatus.orderHeader?.freightTerms ?? 'N/A'}</Typography>
                      <Typography>Shipping Account: {orderStatus.orderHeader?.remarks ?? 'N/A'}</Typography>
                      <Typography>FOB: {orderStatus.orderHeader?.fob ?? 'N/A'}</Typography>
                      <Typography>Credit Terms: {orderStatus.orderHeader?.creditTerms ?? 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {orderStatus.comments && orderStatus.comments.length > 0 && (
                  <Box px={2} py={1}>
                    <Box>
                      <Typography fontWeight="bold" mb={1}>Order Comments:</Typography>
                      {orderStatus.comments.map((comment: Comment, idx: number) => (
                        <Box key={idx} mb={1}>
                          {comment.lines.map((line, lineIdx) => (
                            <Typography
                              key={lineIdx}
                              variant="body2"
                              sx={{ 
                                lineHeight: 1.2,
                                mb: 0.5,
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    <b>Open Items</b>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        ml: 3,
                        color: '#c3183c',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                      onClick={toggleAllOpenRows}
                    >
                      Click to Display Details
                      <ArrowDropDownOutlinedIcon sx={{ fontSize: '1.5rem', ml: 0.5 }} />
                    </Box>
                  </Typography>
                  <Typography variant="body2">
                    <b>Questions?</b>{' '}
                  <a
                    href={`mailto:orders@barksdale.com?subject=Question on Order ${orderStatus.orderHeader?.orderNumber} (Barksdale)`}
                    style={{ color: '#c3183c', marginRight: '8px', textDecoration: 'underline' }}
                  >
                    Contact Customer Care
                  </a>
                  </Typography>
                </Box>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 1, }}
                >
                  <Table size="small" aria-label="user list table">
                    <TableHead sx={{ backgroundColor: "#c3183c", color: '#ffffff' }}>
                      <TableRow>
                      <TableHeadCol>Line</TableHeadCol>
                        <TableHeadCol>Part Number</TableHeadCol>
                        <TableHeadCol>Description</TableHeadCol>
                        <TableHeadCol>Quantity</TableHeadCol>
                        <TableHeadCol>Ship Date</TableHeadCol>
                        <TableHeadCol>Est. Revised Ship Date</TableHeadCol>
                        <TableHeadCol>Net Price</TableHeadCol>
                        <TableHeadCol>Ext. Price</TableHeadCol>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {openItemRows.length > 0 ? (
                          openItemRows.map((item: LineItem, index: number) => (
                          <React.Fragment key={index}>
                            <TableRow key={index}>
                              <TableCol>
                                <IconButton size="small" onClick={() => toggleOpenItem(`${item.lineNumber}_${index}`)}>
                                  {!!openExpand[`${item.lineNumber}_${index}`] ? (
                                    <ArrowDropDownOutlinedIcon sx={{ color: '#c3183c' }} />
                                  ) : (
                                    <ArrowRightOutlinedIcon sx={{ color: '#c3183c' }} />
                                  )}
                                </IconButton>
                                {item.lineNumber}
                              </TableCol>
                              <TableCol>{item.partNumber}</TableCol>
                              <TableCol>{item.description}</TableCol>
                              <TableCol>{item.quantity}</TableCol>
                              <TableCol>
                                {item.dueDateDisplayMessage ? (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#1976d2', 
                                      fontWeight: 'bold',
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    {item.dueDateDisplayMessage}
                                  </Typography>
                                ) : (
                                  moment(item.dueDate).format('MM/DD/YYYY')
                                )}
                              </TableCol>
                              <TableCol>{item.promiseDate ? moment(item.promiseDate).format('MM/DD/YYYY') : ''}</TableCol>
                              <TableCol>{formatCurrency(item.unitPrice)}</TableCol>
                              <TableCol>{formatCurrency(item.extendedPrice)}</TableCol>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0 }}>
                                <Collapse in={!!openExpand[`${item.lineNumber}_${index}`]} timeout="auto" unmountOnExit>
                                  <Box sx={{ bgcolor: '#ebebeb', p: 2, borderTop: '1px solid #ddd' }}>
                                    <Grid container spacing={2}>
                                      <Grid size={4}>
                                        <Typography variant="body2" gutterBottom sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textAlign: 'center' }}>
                                          <b>Ship Via: {item.freightDescription || 'N/A'}</b>
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCol colSpan={8}>
                            This order has no open items.
                          </TableCol>
                        </TableRow>
                      )}
                    </TableBody>

                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'black' }}>
                                              <TableCell colSpan={8} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                        Sub Total Open Items: {formatCurrency(orderStatus.orderTotals?.subtotalOpen ?? 0)}
                      </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" component="div">
                    <b>Shipped Items</b>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        ml: 3,
                        color: '#c3183c',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                      onClick={toggleAllShippedRows}
                    >
                      Click to Display Details
                      <ArrowDropDownOutlinedIcon sx={{ fontSize: '1.5rem', ml: 0.5 }} />
                    </Box>
                  </Typography>
                <Typography variant="body2">
                  <b>Questions?</b>{' '}
                  <a
                    href={`mailto:orders@barksdale.com?subject=Question on Order ${orderStatus.orderHeader?.orderNumber} (Barksdale)`}
                    style={{ color: '#c3183c', marginRight: '8px', textDecoration: 'underline' }}
                  >
                    Contact Customer Care
                  </a>
                </Typography>

                </Box>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 1, }}
                >
                  <Table size="small" aria-label="user list table">
                    <TableHead sx={{ backgroundColor: "#c3183c", color: '#ffffff' }}>
                      <TableRow sx={{ backgroundColor: '#c8102e' }}>
                      <TableHeadCol>Line</TableHeadCol>
                        <TableHeadCol>Part Number</TableHeadCol>
                        <TableHeadCol>Description</TableHeadCol>
                        <TableHeadCol>Quantity</TableHeadCol>
                        <TableHeadCol>Ship Date</TableHeadCol>
                        <TableHeadCol>Net Price</TableHeadCol>
                        <TableHeadCol>Ext. Price</TableHeadCol>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {shippedRows.length > 0 ? (
                        shippedRows.map((item: LineItem, index: number) => (
                          <React.Fragment key={index}>
                            <TableRow>
                              <TableCol>
                                <IconButton size="small" onClick={() => toggleShipItem(`${item.lineNumber}_${index}`)}>
                                  {!!shippedExpand[`${item.lineNumber}_${index}`] ? (
                                    <ArrowDropDownOutlinedIcon sx={{ color: '#c3183c' }} />
                                  ) : (
                                    <ArrowRightOutlinedIcon sx={{ color: '#c3183c' }} />
                                  )}
                                </IconButton>
                                {item.lineNumber}
                              </TableCol>
                              <TableCol>{item.partNumber}</TableCol>
                              <TableCol>{item.description}</TableCol>
                              <TableCol>{item.quantity}</TableCol>
                              <TableCol>{item.shipDate ? moment(item.shipDate).format('MM/DD/YYYY') : ''}</TableCol>
                              <TableCol>{formatCurrency(item.unitPrice)}</TableCol>
                              <TableCol>{formatCurrency(item.extendedPrice)}</TableCol>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0 }}>
                                <Collapse in={!!shippedExpand[`${item.lineNumber}_${index}`]} timeout="auto" unmountOnExit>
                                  <Box sx={{ bgcolor: '#ebebeb', p: 2, borderTop: '1px solid #ddd' }}>
                                    <Grid container spacing={2}>
                                      <Grid size={4}>
                                        <Typography variant="body2" gutterBottom sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textAlign: 'center' }}>
                                          <b>Ship Via: {item.freightDescription || 'N/A'}</b>
                                        </Typography>
                                      </Grid>
                                      <Grid size={4}>
                                        <Typography variant="body2" gutterBottom sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textAlign: 'center' }}>
                                          <b>
                                            {(() => {
                                              const trackingInfo = getTrackingInfo(item);
                                              return (
                                                <>
                                                  {trackingInfo.label}{" "}
                                                  {trackingInfo.url ? (
                                                    <Link
                                                      href={trackingInfo.url}
                                                      target="_blank"
                                                      rel="noopener"
                                                      underline="hover"
                                                      sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textDecoration: 'underline' }}
                                                    >
                                                      {trackingInfo.number}
                                                    </Link>
                                                  ) : (
                                                    <span>{trackingInfo.number}</span>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </b>
                                        </Typography>
                                      </Grid>
                                      <Grid size={4}>
                                        <Typography variant="body2" gutterBottom sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textAlign: 'center' }}>
                                          <b>
                                            Invoice #:{" "}
                                            <Link
                                                component="button"
                                                onClick={() => handleDownloadInvoice(item.invoiceNumber)}
                                                underline="hover"
                                                sx={{ color: '#c3183c', textDecorationColor: '#c3183c', textDecoration: 'underline' }}
                                            >
                                                {item.invoiceNumber || 'N/A'}
                                            </Link>
                                          </b>
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No shipped items available.
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ backgroundColor: 'black' }}>
                                              <TableCell colSpan={7} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                        Sub Total Shipped Items: {formatCurrency(shippedTotal)}
                      </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Grid size={12}>
                  {orderStatus.legalDisclaimers && orderStatus.legalDisclaimers.length > 0 ? (
                    orderStatus.legalDisclaimers.map((disclaimer: string, index: number) => (
                      <Typography
                        key={index}
                        variant="body1"
                        gutterBottom
                        sx={{ mt: index !== 0 ? 1 : 0 }}
                      >
                        {processLegalDisclaimer(disclaimer)}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      No legal disclaimers found.
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', mt: 3 }} onClick={handleBack}>
                    BACK
                  </Button>
                </Grid>
              </Box>
            </Grid>
        </>
      )}
    </DashboardLayout>
  );
}

export default CheckPurchaseOrderStatusDetails
